import {IOverlay, IPolylineGeometry} from "@/types/map";
import {loadModules} from "esri-loader";
import * as THREE from "three";

export default class WallRendere {
  private view!: __esri.SceneView;
  private externalRenderer!: __esri.externalRenderers;
  private webMercatorUtils!: __esri.webMercatorUtils;

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.Camera;
  // private ambient!: THREE.AmbientLight;
  // private sun!: THREE.DirectionalLight;
  private faceNum: number = 100;

  private wallColor: string = "rgba(255,0,0,0.9)";

  constructor(
    view: __esri.SceneView,
    externalRenderer: __esri.externalRenderers,
    webMercatorUtils: __esri.webMercatorUtils
  ) {
    this.view = view;
    this.externalRenderer = externalRenderer;
    this.webMercatorUtils = webMercatorUtils;
  }

  private points = [
    [121.414517402, 31.1649200437],
    [121.4187144042, 31.1669164488],
    [121.430738519, 31.1682408251]
  ];
  private wallHeight = 50;
  private textureOffset = 0;
  private textures: Array<THREE.Texture> = [];

  private wallGeometry: Array<{
    geo: any;
    faces: any[];
    uvs: any[];
    moveIndex: number;
    materials: any[];
  }> = [];

  public async setup(context: any) {
    this.renderer = new THREE.WebGLRenderer({
      context: context.gl,
      premultipliedAlpha: false
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setViewport(0, 0, this.view.width, this.view.height);

    this.renderer.autoClear = false;

    const originalSetRenderTarget = this.renderer.setRenderTarget.bind(
      this.renderer
    );
    this.renderer.setRenderTarget = (target) => {
      originalSetRenderTarget(target);
      if (target === null) {
        context.bindRenderTarget();
      }
    };

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera();

    // this.ambient = new THREE.AmbientLight(0xffffff, 0.5);
    // this.scene.add(this.ambient);
    // this.sun = new THREE.DirectionalLight(0xffffff, 0.5);
    // this.scene.add(this.sun);

    context.resetWebGLState();
  }

  public render(context: any) {
    const cam = context.camera;

    this.camera.position.set(cam.eye[0], cam.eye[1], cam.eye[2]);
    this.camera.up.set(cam.up[0], cam.up[1], cam.up[2]);
    this.camera.lookAt(
      new THREE.Vector3(cam.center[0], cam.center[1], cam.center[2])
    );

    // Projection matrix can be copied directly
    this.camera.projectionMatrix.fromArray(cam.projectionMatrix);

    // if (this.textureOffset <= 0) {
    //   this.textureOffset = 1;
    // } else {
    //   this.textureOffset -= 0.005;
    // }
    // if (this.textures.length > 0) {
    //   this.textures.forEach((texture, index) => {
    //     texture.offset.set(this.textureOffset, 0);
    //   }, this);
    // }
    if (this.wallGeometry.length > 0) {
      this.wallGeometry.forEach((wall) => {
        let nfaces: any[] = [];
        let nUvs: any[] = [];
        for (let i = 0; i < this.faceNum; i++) {
          let f = wall.faces[(i + wall.moveIndex) % wall.faces.length].clone();
          f.materialIndex = i;
          nfaces.push(f);
          nUvs.push(wall.uvs[(i + wall.moveIndex) % wall.faces.length]);
        }
        wall.geo.faces = nfaces;
        wall.geo.faceVertexUvs[0] = nUvs;
        wall.geo.elementsNeedUpdate = true;
        wall.geo.uvsNeedUpdate = true;

        wall.moveIndex = (wall.moveIndex + 2) % wall.faces.length;
      }, this);
    }

    // draw the scene
    /////////////////////////////////////////////////////////////////////////////////////////////////////
    this.renderer.state.reset();
    this.renderer.render(this.scene, this.camera);

    // as we want to smoothly animate the ISS movement, immediately request a re-render
    this.externalRenderer.requestRender(this.view);

    // cleanup
    context.resetWebGLState();
  }

  public updateWallLines(overlays: Array<IOverlay>) {
    overlays.forEach((overlay) => {
      const path = (overlay.geometry as IPolylineGeometry).path;
      const points = path.map((point) => [point.x, point.y]);
      this.buildWall(points);
    });
  }

  public deleteAllWallLines() {
    this.scene.clear();
  }

  private async buildWall(points: Array<Array<number>>) {
    // 计算顶点
    let transform = new THREE.Matrix4(); // 变换矩阵
    let transformation = new Array(16);
    let vector3List: Array<THREE.Vector3> = []; // 顶点数组
    let faceList: Array<THREE.Face3> = []; // 三角面数组
    let faceVectexUvs = []; // 面的uv层队列，该队列用于将纹理和几何信息进行映射
    points = await this.compactLine(points);

    points.forEach((point) => {
      // 将经纬度转换为xy
      let pointXY = this.webMercatorUtils.lngLatToXY(point[0], point[1]);
      // 先转换高度为0的点
      transform.fromArray(
        this.externalRenderer.renderCoordinateTransformAt(
          this.view,
          [pointXY[0], pointXY[1], 0],
          this.view.spatialReference,
          transformation
        )
      );
      vector3List.push(
        new THREE.Vector3(
          transform.elements[12],
          transform.elements[13],
          transform.elements[14]
        )
      );

      // 再转换距离地面高度为height的点
      transform.fromArray(
        this.externalRenderer.renderCoordinateTransformAt(
          this.view,
          [pointXY[0], pointXY[1], this.wallHeight],
          this.view.spatialReference,
          transformation
        )
      );
      vector3List.push(
        new THREE.Vector3(
          transform.elements[12],
          transform.elements[13],
          transform.elements[14]
        )
      );
    });
    // 纹理坐标
    const t0 = new THREE.Vector2(0, 0); // 左下角
    const t1 = new THREE.Vector2(1, 0); // 右下角
    const t2 = new THREE.Vector2(1, 1); // 右上角
    const t3 = new THREE.Vector2(0, 1); // 左上角
    // 生成几何体三角面
    for (let i = 0; i < vector3List.length - 2; i++) {
      if (i % 2 === 0) {
        faceList.push(new THREE.Face3(i, i + 2, i + 1));
        faceVectexUvs.push([t0, t1, t3]);
      } else {
        faceList.push(new THREE.Face3(i, i + 1, i + 2));
        faceVectexUvs.push([t3, t1, t2]);
      }
    }

    // 纹理
    // const alphaMap = new THREE.TextureLoader().load("obj/texture_1.png");
    // const texture = new THREE.TextureLoader().load("obj/texture_2_right.png");
    // texture.wrapS = THREE.RepeatWrapping;
    // texture.wrapT = THREE.RepeatWrapping;
    // texture.offset.set(0, 0.5);
    // this.textures.push(texture);

    // 几何体
    const geometry = new THREE.Geometry();
    geometry.vertices = vector3List;
    geometry.faces = faceList;
    geometry.faceVertexUvs[0] = faceVectexUvs;

    //底部纹理
    let alphacanvas = document.createElement("canvas");
    alphacanvas.width = 100;
    alphacanvas.height = 100;
    alphacanvas.style.backgroundColor = "rgba(0,0,0,192)";
    let alphacontext: any = alphacanvas.getContext("2d");
    let grd: any = alphacontext.createLinearGradient(0, 0, 0, 100);
    grd.addColorStop(0, "	#696969");
    grd.addColorStop(0.6, "black");
    grd.addColorStop(1, "#bebebe");
    alphacontext.fillStyle = grd;
    alphacontext.fillRect(0, 0, 100, 100);
    let alphaMap = new THREE.CanvasTexture(alphacanvas);

    //贴图
    let textcanvas = document.createElement("canvas");
    textcanvas.width = 100;
    textcanvas.height = 100;
    let textct: any = textcanvas.getContext("2d");
    let lingrad: any = textct.createLinearGradient(0, 0, 90, 0);
    lingrad.addColorStop(0.6, "rgba(0,0,0,0)");
    lingrad.addColorStop(1, this.wallColor);
    textct.fillStyle = lingrad;
    textct.fillRect(0, 0, 100, 100);
    let texture = new THREE.CanvasTexture(textcanvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    //texture.offset.set(0, 0.5);
    //this.textures.push(texture);

    const material = new THREE.MeshBasicMaterial({
      color: this.wallColor,
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
      alphaMap
    });
    const mesh = new THREE.Mesh(geometry, material);

    const geometry3 = new THREE.Geometry();
    geometry3.vertices = vector3List;
    let nfaces: any[] = [];
    let materials: any[] = [];
    let newfaceUvs: any[] = [];
    let faceWidth = (100 * 2) / this.faceNum;
    for (let i = 0; i < this.faceNum; i++) {
      let f = faceList[i].clone();
      let uvs = faceVectexUvs[i];
      newfaceUvs.push(uvs);
      f.materialIndex = i;
      nfaces.push(f);
      //切割纹理图片
      let c = document.createElement("canvas");
      c.width = faceWidth;
      c.height = 100;
      let ctx: any = c.getContext("2d");
      ctx.drawImage(
        textcanvas,
        faceWidth * Math.floor(i / 2),
        0,
        faceWidth,
        100,
        0,
        0,
        faceWidth,
        100
      );
      let a = new THREE.CanvasTexture(c);
      a.wrapS = THREE.RepeatWrapping;
      a.wrapT = THREE.RepeatWrapping;
      //a.offset.set(0, 0.5);
      materials.push(
        new THREE.MeshBasicMaterial({
          side: THREE.DoubleSide,
          transparent: true,
          depthWrite: false,
          map: a
        })
      );
    }
    geometry3.faces = nfaces;
    geometry3.faceVertexUvs[0] = newfaceUvs;

    // const geometry2 = geometry.clone();
    // const material2 = new THREE.MeshBasicMaterial({
    //   side: THREE.DoubleSide,
    //   transparent: true,
    //   depthWrite: false,
    //   map: texture
    // });

    const mesh2 = new THREE.Mesh(geometry3, materials);

    this.wallGeometry.push({
      geo: geometry3,
      faces: faceList,
      uvs: faceVectexUvs,
      moveIndex: 0,
      materials
    });

    this.scene.add(mesh);
    this.scene.add(mesh2);
  }
  //致密线
  private async compactLine(points: Array<Array<number>>) {
    const [
      geometryJsonUtils,
      geometryEngine,
      webMercatorUtils
    ] = await loadModules([
      "esri/geometry/support/jsonUtils",
      "esri/geometry/geometryEngine",
      "esri/geometry/support/webMercatorUtils"
    ]);
    let geometry = geometryJsonUtils.fromJSON({
      paths: [points]
    });
    if (
      this.view.spatialReference.isWebMercator &&
      geometry.spatialReference.isWGS84
    ) {
      geometry = webMercatorUtils.geographicToWebMercator(geometry);
    }
    let len = geometryEngine.planarLength(geometry, "meters");
    let num = Math.floor(len / 100);
    let compactPoint = geometryEngine.densify(geometry, num, "meters");
    if (this.view.spatialReference.isWebMercator) {
      compactPoint = webMercatorUtils.webMercatorToGeographic(compactPoint);
    }
    let paths = compactPoint.paths[0] as Array<Array<number>>;
    if (paths.length < 50) {
      this.faceNum = 20;
    } else if (paths.length < 50) {
      this.faceNum = 40;
    } else if (paths.length < 100) {
      this.faceNum = 50;
    }
    return paths;
  }
}
